import * as React from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	BellIcon,
	DocumentTextIcon,
	BanknotesIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';

import { cn } from '../ui';
import {
	useCarBrands,
	useMarkAllNotificationsRead,
	useMarkNotificationRead,
	useNotifications,
	type AppNotification,
} from '../api/hooks';
import { relativeTimeAgo } from '../lib/format';

/** Strongly-typed shapes for the notification kinds we know how to render.
 *  Falls through to a generic display for unknown kinds — see `present()`. */
interface MechanicReportPayload {
	reportId?: number;
	clientName?: string;
	model?: string;
	productionYear?: number;
}
interface QuotationNewPayload {
	requestId?: number;
	quotationId?: number;
	model?: string;
	productionYear?: number;
	brandId?: number;
	itemCount?: number;
	viaInvite?: boolean;
}

/**
 * Bell + dropdown of unread notifications. Polls every 30s via
 * `useNotifications` (refetchInterval). Click on a row marks it read
 * (optimistic — bell count drops instantly) and navigates to the resource.
 *
 * The "Mark all read" link in the panel header bulk-clears the unread list.
 *
 * Mounted in MainLayout's header. Visibility is the caller's concern; this
 * component renders the same way for any role and trusts the server to
 * filter what the user can see (which is the right place for tenant rules).
 */
export function NotificationBell() {
	const { data } = useNotifications({ unreadOnly: true });
	const markRead = useMarkNotificationRead();
	const markAllRead = useMarkAllNotificationsRead();
	const navigate = useNavigate();
	const { t } = useTranslation();

	// Brand lookup for "BMW · 4 series" formatting in the quotation.new title.
	// Cached for an hour by useCarBrands — no extra fetch.
	const { data: brands } = useCarBrands();
	const brandNameById = React.useMemo(() => {
		const m = new Map<number, string>();
		for (const b of (brands || []) as Array<{ id: number; brand_name: string }>) {
			m.set(b.id, b.brand_name);
		}
		return m;
	}, [brands]);

	const items = data?.data ?? [];
	const unreadCount = data?.unreadCount ?? 0;

	const onItemClick = (n: AppNotification) => {
		const target = linkFor(n);
		// Mark read (optimistic) BEFORE navigating so the panel collapses
		// without a stale flicker.
		markRead.mutate(n.id);
		if (target) navigate(target);
	};

	return (
		<Popover className="relative">
			{({ close }) => (
				<>
					<Popover.Button
						className="relative p-2 rounded-md hover:bg-slate-100 text-ink-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
						aria-label={
							unreadCount > 0
								? t('notifications:bell.ariaLabelWithCount', { count: unreadCount })
								: t('notifications:bell.ariaLabel')
						}
					>
						<BellIcon className="h-5 w-5" />
						{unreadCount > 0 && (
							<span
								aria-hidden
								className="absolute top-1 end-1 h-2 w-2 rounded-full bg-danger-500 ring-2 ring-white"
							/>
						)}
					</Popover.Button>
					<Transition
						as={React.Fragment}
						enter="transition ease-out duration-150"
						enterFrom="opacity-0 translate-y-1"
						enterTo="opacity-100 translate-y-0"
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100 translate-y-0"
						leaveTo="opacity-0 translate-y-1"
					>
						{/* `end-0` (inset-inline-end: 0) anchors the dropdown to the
						    direction-aware end of the bell button, so the panel hugs the
						    right edge in LTR and the left edge in RTL. */}
						<Popover.Panel className="absolute end-0 mt-2 w-80 sm:w-96 z-40 origin-top-right rounded-xl bg-white shadow-lift ring-1 ring-slate-200 focus:outline-none">
							<div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
								<h3 className="text-sm font-semibold text-ink">
									{t('notifications:bell.title')}
									{unreadCount > 0 && (
										<span className="ms-2 text-xs font-medium text-ink-muted">
											{t('notifications:bell.unread', { count: unreadCount })}
										</span>
									)}
								</h3>
								{items.length > 0 && (
									<button
										onClick={() => markAllRead.mutate()}
										className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
									>
										<CheckIcon className="h-3.5 w-3.5" />
										{t('notifications:bell.markAllRead')}
									</button>
								)}
							</div>

							{items.length === 0 ? (
								<div className="px-4 py-10 text-center">
									<BellIcon className="h-8 w-8 mx-auto text-ink-faint" />
									<p className="mt-2 text-sm text-ink-muted">
										{t('notifications:bell.emptyTitle')}
									</p>
									<p className="text-xs text-ink-faint mt-1">
										{t('notifications:bell.emptyDescription')}
									</p>
								</div>
							) : (
								<ul className="max-h-96 overflow-y-auto scrollbar-thin divide-y divide-slate-100">
									{items.map((n) => (
										<li key={n.id}>
											<button
												onClick={() => {
													onItemClick(n);
													close();
												}}
												className="w-full text-start px-4 py-3 hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50"
											>
												<NotificationItem
													n={n}
													brandName={(() => {
														const p = (n.payload || {}) as QuotationNewPayload;
														return p.brandId ? brandNameById.get(p.brandId) : undefined;
													})()}
												/>
											</button>
										</li>
									))}
								</ul>
							)}
						</Popover.Panel>
					</Transition>
				</>
			)}
		</Popover>
	);
}

/** Map notification kind → in-app navigation target. */
function linkFor(n: AppNotification): string | null {
	switch (n.kind) {
		case 'mechanic_report.new': {
			const p = (n.payload || {}) as MechanicReportPayload;
			return p.reportId ? `/car-reports/${p.reportId}` : null;
		}
		case 'quotation.new': {
			const p = (n.payload || {}) as QuotationNewPayload;
			return p.requestId ? `/requests/${p.requestId}` : null;
		}
		default:
			return null;
	}
}

function NotificationItem({
	n,
	brandName,
}: {
	n: AppNotification;
	brandName?: string;
}) {
	const { t } = useTranslation();
	const { icon, tone, title, body } = present(n, brandName, t);
	return (
		<div className="flex items-start gap-3">
			<div
				className={cn(
					'h-8 w-8 rounded-full grid place-items-center flex-shrink-0',
					tone
				)}
			>
				{icon}
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-medium text-ink truncate">{title}</p>
				{body && (
					<p className="text-xs text-ink-muted truncate mt-0.5">{body}</p>
				)}
				<p className="text-2xs text-ink-faint mt-1">
					{relativeTimeAgo(n.created_at)}
				</p>
			</div>
		</div>
	);
}

interface Presented {
	icon: React.ReactNode;
	tone: string;
	title: string;
	body?: string;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

function present(n: AppNotification, brandName: string | undefined, t: TFn): Presented {
	switch (n.kind) {
		case 'mechanic_report.new': {
			const p = (n.payload || {}) as MechanicReportPayload;
			return {
				icon: <DocumentTextIcon className="h-4 w-4 text-brand-700" />,
				tone: 'bg-brand-50',
				title: p.clientName
					? t('notifications:kinds.mechanic_report.titleFor', { client: p.clientName })
					: t('notifications:kinds.mechanic_report.titleNoClient'),
				body: [p.model, p.productionYear].filter(Boolean).join(' · ') || undefined,
			};
		}
		case 'quotation.new': {
			const p = (n.payload || {}) as QuotationNewPayload;
			const vehicle = [brandName, p.model].filter(Boolean).join(' ').trim();
			return {
				icon: <BanknotesIcon className="h-4 w-4 text-accent-700" />,
				tone: 'bg-accent-50',
				title: vehicle
					? t('notifications:kinds.quotation.titleFor', { vehicle })
					: t('notifications:kinds.quotation.titleNoVehicle'),
				body:
					p.itemCount != null
						? t('notifications:kinds.quotation.items', { count: p.itemCount })
						: undefined,
			};
		}
		default:
			return {
				icon: <BellIcon className="h-4 w-4 text-ink-muted" />,
				tone: 'bg-slate-100',
				title: n.kind,
			};
	}
}
